import { useState, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { supabase } from "~/integrations/supabase/client";

export interface ClientSalesData {
  clientId: string;
  clientName: string;
  clientTaxId: string;
  totalSales: number;
  invoiceCount: number;
  percentage: number;
}

export interface ClientSalesSummary {
  totalSales: number;
  totalInvoices: number;
  totalClients: number;
  averagePerClient: number;
}

export interface InvoiceDetail {
  bsaleNumber: number;
  clientName: string;
  clientTaxId: string;
  emissionDate: number;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
}

interface RawClientSalesRow {
  client_id: string;
  client_name: string;
  client_tax_id: string;
  total_sales: number;
  invoice_count: number;
}

interface RawInvoiceDetailRow {
  bsale_number: string;
  client_name: string;
  client_tax_id: string;
  emission_date: number;
  net_amount: number;
  tax_amount: number;
  total_amount: number;
}

async function fetchClientSalesSummary(
  startDate: Date,
  endDate: Date
): Promise<RawClientSalesRow[]> {
  const { data, error } = await supabase.rpc("get_client_sales_summary", {
    p_start_date: startDate.toISOString(),
    p_end_date: endDate.toISOString(),
  });

  if (error) {
    console.error("Error fetching client sales summary:", error);
    throw error;
  }

  return (data as RawClientSalesRow[]) || [];
}

async function fetchClientSalesDetails(
  startDate: Date,
  endDate: Date
): Promise<RawInvoiceDetailRow[]> {
  const { data, error } = await supabase.rpc("get_client_sales_details", {
    p_start_date: startDate.toISOString(),
    p_end_date: endDate.toISOString(),
  });

  if (error) {
    console.error("Error fetching client sales details:", error);
    throw error;
  }

  return (data as RawInvoiceDetailRow[]) || [];
}

export const useClientSalesDashboard = () => {
  // Date filters - Default to current month
  const [startDate, setStartDate] = useState<Date | undefined>(
    dayjs().startOf("month").toDate()
  );
  const [endDate, setEndDate] = useState<Date | undefined>(dayjs().toDate());

  // Search filter
  const [searchQuery, setSearchQuery] = useState("");

  // Validate dates
  const validStartDate =
    startDate instanceof Date && !isNaN(startDate.getTime())
      ? startDate
      : dayjs().startOf("month").toDate();
  const validEndDate =
    endDate instanceof Date && !isNaN(endDate.getTime())
      ? endDate
      : dayjs().toDate();

  // Fetch summary data
  const {
    data: rawData = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "client-sales-summary",
      validStartDate.toISOString(),
      validEndDate.toISOString(),
    ],
    queryFn: () => fetchClientSalesSummary(validStartDate, validEndDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Calculate totals and percentages
  const { clients, summary } = useMemo(() => {
    if (!rawData.length) {
      return {
        clients: [],
        summary: {
          totalSales: 0,
          totalInvoices: 0,
          totalClients: 0,
          averagePerClient: 0,
        },
      };
    }

    const totalSales = rawData.reduce(
      (sum, row) => sum + Number(row.total_sales || 0),
      0
    );
    const totalInvoices = rawData.reduce(
      (sum, row) => sum + Number(row.invoice_count || 0),
      0
    );

    const clientsWithPercentage: ClientSalesData[] = rawData.map((row) => ({
      clientId: row.client_id,
      clientName: row.client_name || "Sin nombre",
      clientTaxId: row.client_tax_id || "Sin RUT",
      totalSales: Number(row.total_sales || 0),
      invoiceCount: Number(row.invoice_count || 0),
      percentage: totalSales > 0 ? (Number(row.total_sales || 0) / totalSales) * 100 : 0,
    }));

    return {
      clients: clientsWithPercentage,
      summary: {
        totalSales,
        totalInvoices,
        totalClients: rawData.length,
        averagePerClient: rawData.length > 0 ? totalSales / rawData.length : 0,
      },
    };
  }, [rawData]);

  // Filter clients by search query
  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) return clients;

    const query = searchQuery.toLowerCase();
    return clients.filter(
      (client) =>
        client.clientName.toLowerCase().includes(query) ||
        client.clientTaxId.toLowerCase().includes(query)
    );
  }, [clients, searchQuery]);

  // Function to fetch details for export (lazy-loaded)
  const fetchDetailsForExport = async (): Promise<InvoiceDetail[]> => {
    const rawDetails = await fetchClientSalesDetails(validStartDate, validEndDate);
    return rawDetails.map((row) => ({
      bsaleNumber: row.bsale_number,
      clientName: row.client_name || "Sin nombre",
      clientTaxId: row.client_tax_id || "Sin RUT",
      emissionDate: row.emission_date,
      netAmount: Number(row.net_amount || 0),
      taxAmount: Number(row.tax_amount || 0),
      totalAmount: Number(row.total_amount || 0),
    }));
  };

  return {
    clients: filteredClients,
    allClients: clients,
    summary,
    isLoading,
    error: error ? String(error) : null,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    searchQuery,
    setSearchQuery,
    refetch,
    fetchDetailsForExport,
  };
};
